import { useState, useEffect, useRef } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { apiFetch } from '../../lib/api';
import { ENDPOINTS } from '../../constants/endpoints';
import { CHAT_ROLE } from '../../constants/chat-roles';
import { ChatMessage } from '../../interfaces/chat-message';
import { CourseAnalysis } from '../../interfaces/course-analysis';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { ScrollArea } from '../../components/ui/scroll-area';
import { Send } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import { useWorkspace } from '../../context/workspace-context';

export default function CourseAnalysisPage() {
  const { id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const navigationState = (location.state ?? {}) as {
    questionTypes?: string[];
    numLevels?: number | string;
    questionsPerLevel?: number | string;
  };
  const questionTypes = navigationState.questionTypes;
  const numLevels = navigationState.numLevels;
  const questionsPerLevel = navigationState.questionsPerLevel;
  const { currentWorkspace, isLoadingWorkspaces } = useWorkspace();
  const [courseName, setCourseName] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [courseAnalysis, setCourseAnalysis] = useState<CourseAnalysis | null>(
    null,
  );
  const [chatMessages, setChatMessages] = useState<ChatMessage[]>([]);
  const [inputMessage, setInputMessage] = useState('');
  const [conversationId, setConversationId] = useState<string | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const dataFetchedRef = useRef<boolean>(false);
  const savingAnalysisRef = useRef<boolean>(false);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  // Scroll to bottom when messages change
  useEffect(() => {
    scrollToBottom();
  }, [chatMessages]);

  const resetTextarea = () => {
    if (textareaRef.current) {
      textareaRef.current.style.height = '50px'; // Reset to min height
    }
  };

  useEffect(() => {
    async function courseHasLevels(): Promise<boolean> {
      try {
        const response = await apiFetch(
          ENDPOINTS.COURSE_HAS_LEVELS(Number(id), currentWorkspace!.id),
        );
        if (!response.ok) return false;

        const data = await response.json();
        return data.hasLevels === true;
      } catch {
        return false;
      }
    }

    async function initialize() {
      try {
        setIsAnalyzing(true);

        setChatMessages([
          {
            role: CHAT_ROLE.ASSISTANT,
            content:
              'Analyzing your course information to create a structure...',
          },
        ]);

        const response = await apiFetch(ENDPOINTS.COURSE_ANALYSIS_INITIALIZE, {
          method: 'POST',
          body: JSON.stringify({
            courseId: Number(id),
            organisationId: currentWorkspace!.id,
            numLevels: numLevels ? Number(numLevels) : undefined,
            questionsPerLevel: questionsPerLevel
              ? Number(questionsPerLevel)
              : undefined,
            questionTypes: questionTypes || undefined,
          }),
        });

        if (!response.ok) {
          throw new Error('Failed to initialize analysis');
        }

        const data = await response.json();

        if (data.courseName) {
          setCourseName(data.courseName);
        }

        if (data.conversationId) {
          setConversationId(data.conversationId);
        }

        if (data.conversation) {
          setChatMessages((prev) => [
            ...prev,
            { role: CHAT_ROLE.ASSISTANT, content: data.conversation },
          ]);
        }

        if (data.analysis) {
          setCourseAnalysis({ analysis: data.analysis });
        }
      } catch (err) {
        setError(
          err instanceof Error ? err.message : 'Failed to initialize analysis',
        );
      } finally {
        setIsAnalyzing(false);
      }
    }

    async function start() {
      if (dataFetchedRef.current) return;
      if (!currentWorkspace?.id) return;
      dataFetchedRef.current = true;

      if (await courseHasLevels()) {
        navigate(`/edit/${id}`, { replace: true });
        return;
      }

      await initialize();
    }

    if (id && currentWorkspace?.id) {
      start();
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, currentWorkspace?.id]);

  const handleSendMessage = async () => {
    if (!inputMessage.trim()) return;
    await sendMessage(inputMessage);
  };

  // Track in-flight messages to prevent duplicates
  const pendingMessagesRef = useRef<Set<string>>(new Set());

  const sendMessage = async (message: string) => {
    const messageKey = `${conversationId}:${message}`;

    if (pendingMessagesRef.current.has(messageKey)) {
      return;
    }

    pendingMessagesRef.current.add(messageKey);
    setIsAnalyzing(true);

    try {
      setChatMessages((prev) => [
        ...prev,
        { role: CHAT_ROLE.USER, content: message },
      ]);
      setInputMessage('');
      resetTextarea();

      const response = await apiFetch(ENDPOINTS.COURSE_ANALYSIS, {
        method: 'POST',
        body: JSON.stringify({
          courseId: Number(id),
          organisationId: currentWorkspace?.id,
          conversationId,
          message,
          currentAnalysis: courseAnalysis,
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to send message');
      }

      const data = await response.json();

      if (data.conversationId) {
        setConversationId(data.conversationId);
      }

      if (data.conversation) {
        setChatMessages((prev) => [
          ...prev,
          { role: CHAT_ROLE.ASSISTANT, content: data.conversation },
        ]);
      }

      if (data.analysis) {
        setCourseAnalysis({ analysis: data.analysis });

        setChatMessages((prev) => [
          ...prev,
          {
            role: CHAT_ROLE.SYSTEM,
            content:
              'The course analysis has been updated based on our conversation.',
          },
        ]);
      }
    } catch (error) {
      setChatMessages((prev) => [
        ...prev,
        {
          role: CHAT_ROLE.ASSISTANT,
          content: `Sorry, I encountered an error: ${
            error instanceof Error ? error.message : 'Unknown error'
          }. Please try again.`,
        },
      ]);
    } finally {
      setIsAnalyzing(false);
      pendingMessagesRef.current.delete(messageKey);
    }
  };

  const handleSaveAnalysis = async () => {
    if (!courseAnalysis || !id) return;

    if (savingAnalysisRef.current) {
      return;
    }
    savingAnalysisRef.current = true;

    try {
      setChatMessages((prev) => [
        ...prev,
        {
          role: CHAT_ROLE.ASSISTANT,
          content:
            "I'm saving the course analysis and preparing to generate questions...",
        },
      ]);
      setIsAnalyzing(true);

      const response = await apiFetch(ENDPOINTS.COURSE_ANALYSIS_SAVE, {
        method: 'POST',
        body: JSON.stringify({
          courseId: Number(id),
          organisationId: currentWorkspace?.id,
          analysis: courseAnalysis,
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to save analysis');
      }

      setChatMessages((prev) => [
        ...prev,
        {
          role: CHAT_ROLE.ASSISTANT,
          content:
            'Analysis saved successfully! Redirecting to question generation...',
        },
      ]);

      await new Promise((resolve) => setTimeout(resolve, 1500));
      navigate(`/edit/${id}`);
    } catch (error) {
      setChatMessages((prev) => [
        ...prev,
        {
          role: CHAT_ROLE.ASSISTANT,
          content:
            'Sorry, I encountered an error while saving the analysis. Please try again.',
        },
      ]);
    } finally {
      setIsAnalyzing(false);
      savingAnalysisRef.current = false;
    }
  };

  if (error) {
    return <div className="p-4 text-red-500">{error}</div>;
  }

  if (
    isLoadingWorkspaces ||
    (!courseName && !error && !isAnalyzing && chatMessages.length === 0)
  ) {
    return (
      <div className="h-screen flex items-center justify-center">
        <div className="flex flex-col items-center gap-4 text-center">
          <img
            src="/images/loading.svg"
            alt="Loading"
            className="w-40 h-auto"
          />
          <div>
            <p className="font-medium text-xl">Loading Course</p>
            <p className="text-sm text-muted-foreground mt-1">
              Preparing for course analysis...
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="h-screen flex flex-col">
      <h1 className="text-2xl font-bold p-4">Course Analysis</h1>
      <div className="flex-1 grid grid-cols-1 md:grid-cols-2 gap-4 p-4 overflow-hidden">
        {/* Chat Card - Left Side - Fixed */}
        <div className="h-[calc(100vh-11rem)] flex flex-col">
          <Card className="flex-1 flex flex-col overflow-hidden border-0">
            <CardHeader>
              <CardTitle>Chat Assistant</CardTitle>
            </CardHeader>
            <CardContent className="flex-1 flex flex-col p-4 overflow-hidden">
              <ScrollArea className="flex-1 pr-4 -mr-4">
                <div className="flex flex-col gap-2">
                  {chatMessages
                    .filter((message) => message.role !== CHAT_ROLE.SYSTEM)
                    .map((message, index) => {
                      // Extract the friendly explanation part if this is an analysis update
                      let displayContent = message.content;
                      if (
                        message.role === CHAT_ROLE.ASSISTANT &&
                        message.content.includes('[ANALYSIS_UPDATE]')
                      ) {
                        const parts =
                          message.content.split('[/ANALYSIS_UPDATE]');
                        if (parts.length > 1) {
                          displayContent = parts[1].trim();
                        }
                      }

                      return (
                        <div
                          key={index}
                          className={`flex ${
                            message.role === CHAT_ROLE.USER
                              ? 'justify-end'
                              : 'justify-start'
                          }`}
                        >
                          {message.role === CHAT_ROLE.ASSISTANT && (
                            <img
                              src="/images/llaami-orange.svg"
                              alt="Llaami"
                              className="w-8 h-8 mr-2 self-end"
                            />
                          )}
                          <div className={`max-w-[80%]`}>
                            {message.role === CHAT_ROLE.ASSISTANT &&
                              message.content.includes('[ANALYSIS_UPDATE]') && (
                                <div className="text-xs text-muted-foreground mb-1 italic">
                                  Analysis Updated ✨
                                </div>
                              )}
                            <div
                              className={`p-3 rounded-lg ${
                                message.role === CHAT_ROLE.USER
                                  ? 'bg-[#A78BFA] text-white'
                                  : 'bg-[#FDF8F3]'
                              }`}
                            >
                              {message.role === CHAT_ROLE.ASSISTANT ? (
                                <div>
                                  <ReactMarkdown
                                    className="prose prose-sm max-w-none dark:prose-invert"
                                    components={{
                                      // Customize link styling
                                      a: ({ node, ...props }) => (
                                        <a
                                          {...props}
                                          className="text-blue-500 hover:text-blue-700"
                                          target="_blank"
                                          rel="noopener noreferrer"
                                        />
                                      ),
                                      // Ensure lists are properly styled
                                      ul: ({ node, ...props }) => (
                                        <ul
                                          {...props}
                                          className="list-disc pl-4 my-2"
                                        />
                                      ),
                                      ol: ({ node, ...props }) => (
                                        <ol
                                          {...props}
                                          className="list-decimal pl-4 my-2"
                                        />
                                      ),
                                      // Style code blocks
                                      code: ({ node, ...props }) => (
                                        <code
                                          {...props}
                                          className="bg-gray-200 dark:bg-gray-800 rounded px-1 py-0.5"
                                        />
                                      ),
                                    }}
                                  >
                                    {displayContent}
                                  </ReactMarkdown>
                                </div>
                              ) : (
                                <span>{displayContent}</span>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  <div ref={messagesEndRef} />
                </div>
                {isAnalyzing && (
                  <div className="flex items-center justify-center p-4">
                    <div className="flex items-center">
                      <div className="rounded-lg p-3 bg-[#FDF8F3]">
                        <div className="flex items-center gap-2">
                          <div className="h-1.5 w-1.5 bg-gray-400 rounded-full animate-bounce [animation-delay:-0.3s]"></div>
                          <div className="h-1.5 w-1.5 bg-gray-400 rounded-full animate-bounce [animation-delay:-0.15s]"></div>
                          <div className="h-1.5 w-1.5 bg-gray-400 rounded-full animate-bounce"></div>
                          <span className="text-sm ml-2 text-gray-500">
                            {chatMessages.length <= 1
                              ? 'AI is creating course structure...'
                              : 'Waiting for response...'}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </ScrollArea>
              <div className="mt-4 pt-4 border-t">
                <div className="relative">
                  <textarea
                    ref={textareaRef}
                    value={inputMessage}
                    onChange={(e) => {
                      setInputMessage(e.target.value);
                      e.target.style.height = 'inherit';
                      e.target.style.height = `${Math.min(
                        e.target.scrollHeight,
                        200,
                      )}px`;
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault();
                        handleSendMessage();
                      }
                    }}
                    placeholder={
                      isAnalyzing
                        ? 'Please wait for analysis to complete...'
                        : 'Ask about the course analysis...'
                    }
                    className={`w-full resize-none rounded-lg pr-12 py-3 px-4 focus:outline-none bg-background min-h-[50px] max-h-[200px] border ${
                      isAnalyzing ? 'bg-gray-50 text-gray-400' : ''
                    }`}
                    rows={1}
                    disabled={isAnalyzing}
                  />
                  <Button
                    onClick={handleSendMessage}
                    disabled={isAnalyzing}
                    className="absolute right-2 bottom-2 h-8 w-8 p-0"
                    variant="ghost"
                  >
                    <Send
                      className={`h-4 w-4 ${
                        isAnalyzing ? 'text-muted' : 'text-primary'
                      }`}
                    />
                  </Button>
                </div>
                <div className="text-xs text-muted-foreground mt-2 text-center">
                  {isAnalyzing && chatMessages.length <= 1
                    ? 'AI is creating course structure... This may take a minute...'
                    : isAnalyzing
                      ? 'Llaami is thinking...'
                      : 'Press Enter to send, Shift + Enter for new line'}
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Course Summary Card - Right Side - Scrollable */}
        <div className="h-[calc(100vh-11rem)] flex flex-col">
          <Card className="flex-1 flex flex-col overflow-hidden border-0">
            <CardHeader>
              <CardTitle>Course Summary</CardTitle>
            </CardHeader>
            <CardContent className="flex-1 overflow-y-auto">
              {isAnalyzing ? (
                <div className="flex items-center justify-center p-8">
                  <div className="flex flex-col items-center gap-4 text-center">
                    {/* Larger spinner for initial analysis */}
                    {chatMessages.length <= 1 ? (
                      <>
                        <img
                          src="/images/loading.svg"
                          alt="Loading"
                          className="w-40 h-auto"
                        />
                        <div>
                          <p className="font-medium text-lg">
                            Creating Course Structure
                          </p>
                          <p className="text-sm text-muted-foreground mt-2 max-w-xs">
                            AI is analyzing your course information. This may
                            take a minute...
                          </p>
                        </div>
                      </>
                    ) : (
                      <>
                        <img
                          src="/images/loading.svg"
                          alt="Loading"
                          className="w-40 h-auto"
                        />
                        <div>
                          <p className="font-medium text-lg">
                            Updating Course Structure
                          </p>
                          <p className="text-sm text-muted-foreground mt-1 max-w-xs">
                            AI is processing your input...
                          </p>
                        </div>
                      </>
                    )}
                  </div>
                </div>
              ) : courseAnalysis?.analysis ? (
                <div className="space-y-6">
                  <div>
                    <h3 className="font-semibold text-lg mb-3">
                      Content Analysis
                    </h3>
                    <div className="bg-[#FDF8F3] p-4 rounded-lg">
                      <p className="mb-2">
                        {courseAnalysis.analysis.contentSummary ||
                          'No content summary available'}
                      </p>
                      <div className="mt-4">
                        <strong>Main Topics:</strong>
                        <ul className="list-disc list-inside ml-2">
                          {courseAnalysis.analysis.mainTopics?.map(
                            (topic, index) => <li key={index}>{topic}</li>,
                          ) || <li>No topics available</li>}
                        </ul>
                      </div>
                    </div>
                  </div>

                  <div>
                    <h3 className="font-semibold text-lg mb-3">
                      Level Structure
                    </h3>
                    <div className="space-y-3">
                      {courseAnalysis.analysis.recommendedStructure?.levels?.map(
                        (level, index) => (
                          <div
                            key={index}
                            className="border border-[#E6E6E6] p-4 rounded-lg"
                          >
                            <h4 className="font-medium ">
                              {level.name || `Level ${index + 1}`}
                            </h4>
                            <p className="text-sm text-muted-foreground">
                              {level.description || 'No description available'}
                            </p>
                            <div className="mt-2">
                              <strong className="text-sm">Topics:</strong>
                              <ul className="list-disc list-inside ml-2">
                                {level.topics?.map((topic, idx) => (
                                  <li
                                    key={idx}
                                    className="text-sm text-muted-foreground"
                                  >
                                    {topic}
                                  </li>
                                )) || (
                                  <li className="text-sm text-muted-foreground">
                                    No topics available
                                  </li>
                                )}
                              </ul>
                            </div>
                            <div className="mt-2 flex justify-between items-center">
                              <div>
                                <strong className="text-sm">Difficulty:</strong>
                                <p className="text-sm text-muted-foreground">
                                  {level.difficulty || 'Not specified'}
                                </p>
                              </div>
                              <div className="text-right">
                                <strong className="text-sm">Questions:</strong>
                                <p className="text-sm text-muted-foreground">
                                  {level.questionCount || 0} questions
                                </p>
                              </div>
                            </div>
                            <div className="mt-2">
                              <strong className="text-sm">
                                Question Distribution:
                              </strong>
                              <div className="grid grid-cols-3 gap-2 mt-1">
                                <div className="text-sm text-muted-foreground">
                                  Single Choice:{' '}
                                  {level.questionDistribution?.singleSelect ||
                                    0}
                                </div>
                                <div className="text-sm text-muted-foreground">
                                  Multiple Choice:{' '}
                                  {level.questionDistribution?.multipleSelect ||
                                    0}
                                </div>
                                <div className="text-sm text-muted-foreground">
                                  Fill in Blank:{' '}
                                  {level.questionDistribution?.fillInBlank || 0}
                                </div>
                              </div>
                            </div>
                          </div>
                        ),
                      ) || (
                        <div className="text-sm text-muted-foreground">
                          No levels defined
                        </div>
                      )}
                    </div>
                  </div>

                  <div>
                    <h3 className="font-semibold text-lg mb-3">
                      Question Strategy
                    </h3>
                    <div className="bg-[#FDF8F3] p-4 rounded-lg">
                      <div className="grid grid-cols-3 gap-4 mb-4">
                        <div className="text-center">
                          <p className="font-medium">Single Choice</p>
                          <p className="text-2xl font-semibold">
                            {courseAnalysis.analysis.questionStrategy
                              ?.totalSingleSelect || 0}
                          </p>
                        </div>
                        <div className="text-center">
                          <p className="font-medium">Multiple Choice</p>
                          <p className="text-2xl font-semibold">
                            {courseAnalysis.analysis.questionStrategy
                              ?.totalMultipleSelect || 0}
                          </p>
                        </div>
                        <div className="text-center">
                          <p className="font-medium">Fill in Blank</p>
                          <p className="text-2xl font-semibold">
                            {courseAnalysis.analysis.questionStrategy
                              ?.totalFillInBlank || 0}
                          </p>
                        </div>
                      </div>
                      <p className="text-sm text-muted-foreground">
                        {courseAnalysis.analysis.questionStrategy?.reasoning ||
                          'No reasoning provided'}
                      </p>
                    </div>
                  </div>

                  <Button
                    onClick={handleSaveAnalysis}
                    className="w-full bg-[#A78BFA] hover:bg-[#A78BFA]/90 text-white rounded-full py-3 mt-6"
                    disabled={isAnalyzing}
                  >
                    {isAnalyzing ? (
                      <div className="flex items-center gap-2">
                        <div className="h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                        <span>Saving Analysis...</span>
                      </div>
                    ) : (
                      'Save Analysis & Generate Questions'
                    )}
                  </Button>
                </div>
              ) : (
                <div className="flex items-center justify-center p-8">
                  <div className="flex flex-col items-center gap-4 text-center">
                    <img
                      src="/images/loading.svg"
                      alt="Loading"
                      className="w-40 h-auto"
                    />
                    <div>
                      <p className="font-medium text-lg">
                        Creating Course Structure
                      </p>
                      <p className="text-sm text-muted-foreground mt-2 max-w-xs">
                        AI is analyzing your course information. This may take a
                        minute...
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
